/**
 * Forced interaction visuals for scripted sims — the demo side of the
 * `forceHover` / `forcePressed` mechanism.
 *
 * ## Why this exists
 *
 * A scripted sim moves a fake cursor across the screen and appears to click
 * things. No real pointer is involved, so CSS `:hover` / `:active` never fire
 * and the control stays visually at rest while the cursor lands on it. The old
 * fix was `forceHover` / `forcePressed` props baked into the LOCAL primitives
 * in `components/ui/*`, which is why every sim was pinned to those copies and
 * could not move to `@solomon-labs/design`.
 *
 * The design system does not accept those props, and should not: they are demo
 * scaffolding, not a control's public API. What it does ship is a named
 * `data-slot` on every element a `Button` renders (`button`, `button-face`,
 * `button-lift-strip`, `button-fill`, `button-label`, …), added explicitly so a
 * demo can address a Button's internals from outside. See
 * `.claude/design-system/docs/components/button.intent.md` → "Addressable parts".
 *
 * So the forcing moved outside the component:
 *
 *   1. the sim puts `data-sim-force="hover" | "press"` on the control's ROOT
 *      (every DS control spreads unknown `data-*` straight through), and
 *   2. one demo-only stylesheet, `styles/sim-force-state.css`, repaints the
 *      hovered / pressed visuals off that attribute by targeting the slots.
 *
 * All the coupling to Button internals lives in that one stylesheet. Call sites
 * spread this helper and know nothing about slots:
 *
 * ```tsx
 * <Button interaction="light-bar" {...simForce(demoSubmitState)}>Buy</Button>
 * ```
 *
 * ## Rules
 *
 * - This paints a VISUAL only. It never changes state, focus, or behavior, and
 *   nothing may read it back — it is an output of the choreography, never an
 *   input to it.
 * - It is inert everywhere the attribute is absent, which is all of production.
 * - It is not a pointer: `data-sim-cursor-key` still anchors where the cursor
 *   travels to. The two are set on the same element and stay independent.
 */

/** The interaction visual a sim is currently forcing on one control. */
export type SimForcedInteraction = "hover" | "press";

/** Props spread onto a control's root to force its interaction visual. */
export interface SimForceStateProps {
    "data-sim-force"?: SimForcedInteraction;
}

/**
 * Turn a sim's forced-interaction phase into props for a control's root.
 *
 * Accepts the phase vocabulary the orchestrators already use (`"idle"` and
 * friends) and emits nothing for anything that is not an active force, so a
 * call site never needs a conditional.
 */
export function simForce(state: SimForcedInteraction | "idle" | false | null | undefined): SimForceStateProps {
    return state === "hover" || state === "press" ? { "data-sim-force": state } : {};
}
